import React, { useState } from "react";
import Seo from "@/components/Seo";
import { SEO } from "@/lib/seoConfig";
import { Link, useParams, Navigate } from "react-router-dom";
import { Section, Kicker, SerifTitle } from "@/components/Section";
import { EditorialImage } from "@/components/EditorialImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PRINT_FRAMES, PRINT_PAPERS, PRINT_SIZES } from "@/lib/data";
import { usePrint } from "@/lib/content";
import { PublicAPI } from "@/lib/api";

export default function PrintDetail() {
    const { id } = useParams();
    const print = usePrint(id);
    const [size, setSize] = useState(PRINT_SIZES[1]);
    const [paper, setPaper] = useState(PRINT_PAPERS[0]);
    const [frame, setFrame] = useState(PRINT_FRAMES[0]);
    const [qty, setQty] = useState(1);
    const [form, setForm] = useState({ full_name: "", email: "", phone: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    if (!print) return <Navigate to="/atelier" replace />;

    const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.full_name || !form.email) {
            toast.error("Please add your name and email.");
            return;
        }
        try {
            setSubmitting(true);
            const cleaned = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" ? null : v]));
            await PublicAPI.createPrintInquiry({
                print_id: print.id,
                print_title: print.title,
                ...cleaned,
                size,
                paper_type: paper,
                framed: frame,
                quantity: qty,
            });
            setSubmitted(true);
            toast.success("Print request received — the studio will respond shortly.");
        } catch (err) {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="pt-24 sm:pt-28">
            <Seo
                {...SEO.print({
                    title: print.title,
                    slug: print.id,
                    description: print.statement,
                })}
                ogImage={print.image}
            />
            <Section spacing="normal">
                <Link to="/atelier" className="kicker lux-underline">← Back to The Atelier</Link>
                <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start" data-testid="print-detail">
                    <div className="lg:col-span-7">
                        <EditorialImage src={print.image} alt={print.title} natural />
                    </div>
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-2">
                            <Badge variant="outline" className="border-[hsl(var(--lux-brass))]/40 text-[hsl(var(--lux-brass))] uppercase tracking-[0.16em] text-[10px]">
                                {print.edition}
                            </Badge>
                            <span className="kicker">{print.collection}</span>
                        </div>
                        <h1 className="font-serif-display text-3xl sm:text-4xl mt-3 leading-tight">{print.title}</h1>
                        <Separator className="my-5 bg-[hsl(var(--lux-brass))]/40 w-10" />
                        <p className="text-sm leading-[1.85] text-muted-foreground">{print.statement}</p>
                        <div className="mt-5 text-sm">
                            <span className="text-muted-foreground">Starting at</span>{" "}
                            <span className="font-serif-display text-xl ml-1">{print.startingAt}</span>
                        </div>

                        {submitted ? (
                            <div data-testid="print-inquire-success" className="mt-8 border border-[hsl(var(--lux-brass))]/40 bg-[hsl(var(--lux-porcelain))] p-7 rounded-[2px]">
                                <div className="kicker">Thank you</div>
                                <h3 className="font-serif-display text-2xl mt-2">Your print request has been received.</h3>
                                <p className="text-sm text-muted-foreground mt-3">The studio will respond within 1–2 business days with pricing, edition availability, and shipping options.</p>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="mt-8 space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field label="Size">
                                        <Select value={size} onValueChange={setSize}>
                                            <SelectTrigger data-testid="print-size-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {PRINT_SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Paper">
                                        <Select value={paper} onValueChange={setPaper}>
                                            <SelectTrigger data-testid="print-paper-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {PRINT_PAPERS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Framing">
                                        <Select value={frame} onValueChange={setFrame}>
                                            <SelectTrigger data-testid="print-frame-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {PRINT_FRAMES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Quantity">
                                        <Input type="number" min={1} max={20} value={qty} onChange={(e) => setQty(parseInt(e.target.value || "1"))} />
                                    </Field>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field label="Full name"><Input value={form.full_name} onChange={onChange("full_name")} required data-testid="print-field-name" /></Field>
                                    <Field label="Email"><Input type="email" value={form.email} onChange={onChange("email")} required data-testid="print-field-email" /></Field>
                                </div>
                                <Field label="Phone (optional)"><Input value={form.phone} onChange={onChange("phone")} /></Field>
                                <Field label="Notes (room, light, occasion)"><Textarea rows={3} value={form.message} onChange={onChange("message")} /></Field>
                                <button
                                    data-testid="print-inquire-button"
                                    type="submit"
                                    disabled={submitting}
                                    className="btn-request w-full sm:w-auto"
                                >
                                    {submitting ? "Sending…" : "Request this print"}
                                </button>
                                <p className="text-xs text-muted-foreground">All editions are signed and accompanied by an archival certificate.</p>
                            </form>
                        )}
                    </div>
                </div>
            </Section>
        </div>
    );
}

const Field = ({ label, children }) => (
    <label className="block">
        <span className="kicker block mb-2">{label}</span>
        {children}
    </label>
);
